import React, { createContext, useState, useEffect } from 'react';
import { sseService } from '../services/sse';
import { inscreverPush } from '../services/push';

export const PedidosContext = createContext();

export function PedidosProvider({ children }) {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_API_URL; // URL do backend do .env

  const fetchPedidos = async () => {
    try {
      const token = localStorage.getItem('token');
      const estabelecimentoId = localStorage.getItem('estabelecimento_id');

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Admin pode ter mais de um estabelecimento — manda qual está
      // selecionado no painel pro backend não assumir o primeiro dele.
      const url = estabelecimentoId
        ? `${API_URL}/buscapedidos?estabelecimento_id=${estabelecimentoId}`
        : `${API_URL}/buscapedidos`;

      const response = await fetch(url, {
        method: 'GET',
        headers,
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log("Pedidos recebidos do servidor:", data);

      const pedidosServidor = Array.isArray(data) ? data : [];

      // Se o cliente tem pedidos mas SSE não está conectado, conectar usando o estabelecimento do pedido
      if (!sseService.isConnected() && pedidosServidor.length > 0) {
        const estabId = pedidosServidor[0].id_estabelecimento;
        if (estabId) {
          localStorage.setItem('estabelecimento_id', String(estabId));
          conectarSSE(String(estabId));
          console.log(`🔌 [Context] SSE conectado via pedido existente - estabelecimento ${estabId}`);
        }
      }

      // Mesclar pedidos: manter pedidos do SSE que ainda não estão no servidor
      setPedidos((prevPedidos) => {
        const idsServidor = new Set(pedidosServidor.map(p => p.id_pedido));

        // Pedidos que vieram via SSE mas ainda não estão no servidor
        const pedidosSSE = prevPedidos.filter(p => !idsServidor.has(p.id_pedido));

        // Combinar: SSE primeiro (mais recentes), depois servidor
        const pedidosMesclados = [...pedidosSSE, ...pedidosServidor];

        // Ordenar por data, mais recente primeiro
        pedidosMesclados.sort((a, b) => new Date(b.data_pedido) - new Date(a.data_pedido));

        console.log(`📦 Pedidos mesclados: ${pedidosSSE.length} do SSE + ${pedidosServidor.length} do servidor = ${pedidosMesclados.length} total`);

        return pedidosMesclados;
      });
    } catch (error) {
      console.error("Erro ao buscar pedidos:", error);
    } finally {
      setLoading(false);
    }
  };

  // Função para atualizar pedidos manualmente
  const atualizarPedidos = async () => {
    console.log('🔄 Atualizando pedidos...');
    await fetchPedidos();
  };

  // Função para conectar ao SSE do estabelecimento
  const conectarSSE = (estabelecimentoId) => {
    if (estabelecimentoId) {
      sseService.connect(estabelecimentoId);
      console.log(`🔌 [Context] Conectado ao SSE do estabelecimento ${estabelecimentoId}`);
    }
  };

  // Notificações push (mobile): igual o SSE, mas continuam chegando com o
  // app fechado/em segundo plano. Cliente logado se inscreve pelos próprios
  // pedidos; cozinha/garçom/admin se inscrevem pelo estabelecimento inteiro.
  const configurarPush = () => {
    if (!localStorage.getItem('token')) return;

    const idCliente = localStorage.getItem('id_cliente');
    const idEstabelecimento = localStorage.getItem('estabelecimento_id');

    if (idCliente) {
      inscreverPush({ papel: 'cliente', idCliente });
    } else if (idEstabelecimento) {
      inscreverPush({ papel: 'equipe', idEstabelecimento });
    }
  };

  useEffect(() => {
    configurarPush();
  }, []);

  useEffect(() => {
    fetchPedidos();

    // 📢 Listener para recarregar pedidos após login
    const handleLoginSuccess = () => {
      console.log('📢 [Context] Evento loginSuccess recebido, recarregando pedidos...');
      fetchPedidos();
      configurarPush();
    };

    // 📢 Listener para reconectar SSE quando estabelecimento mudar
    const handleEstabelecimentoChanged = (event) => {
      const novoEstabelecimentoId = event.detail?.estabelecimentoId;
      console.log('📢 [Context] Evento estabelecimentoChanged recebido:', novoEstabelecimentoId);
      if (novoEstabelecimentoId) {
        conectarSSE(novoEstabelecimentoId);
        configurarPush();
      }
    };

    window.addEventListener('loginSuccess', handleLoginSuccess);
    window.addEventListener('estabelecimentoChanged', handleEstabelecimentoChanged);

    // 🔌 Usar SSE para receber atualizações em tempo real
    // Obter id do estabelecimento do localStorage
    const estabelecimentoId = localStorage.getItem('estabelecimento_id');

    // Conectar ao SSE se já tiver estabelecimento
    conectarSSE(estabelecimentoId);

    // Handler para novos pedidos
    const handleNovoPedido = (data) => {
      console.log('🔔 [Context] Novo pedido recebido:', data);

      const novoPedido = data.pedido;

      // Pegar clienteId atualizado do localStorage
      const clienteId = localStorage.getItem('id_cliente');

      console.log('🔍 [Context] clienteId do localStorage:', clienteId, 'tipo:', typeof clienteId);
      console.log('🔍 [Context] novoPedido.id_cliente:', novoPedido?.id_cliente, 'tipo:', typeof novoPedido?.id_cliente);

      // Se for cliente, garantir que é o pedido dele
      if (clienteId && String(novoPedido.id_cliente) !== clienteId) {
        console.log('⚠️ [Context] Pedido ignorado - não pertence ao cliente logado');
        return;
      }

      console.log('✅ [Context] Adicionando pedido ao state...');

      setPedidos((prevPedidos) => {
        // Evita duplicar pedido
        const existe = prevPedidos.some(
          (p) => p.id_pedido === novoPedido.id_pedido
        );

        if (existe) {
          console.log('⚠️ [Context] Pedido já existe no state, ignorando duplicata');
          return prevPedidos;
        }

        console.log('✅ [Context] Pedido adicionado! Total agora:', prevPedidos.length + 1);
        return [novoPedido, ...prevPedidos];
      });
    };


    // Handler para atualizações de pedido
    const handlePedidoAtualizado = (data) => {
      console.log('🔔 [Context] Pedido atualizado:', data);

      // Notificar o cliente via navegador se o pedido for dele
      const clienteId = localStorage.getItem('id_cliente');
      if (clienteId && data.pedido && String(data.pedido.id_cliente) === clienteId) {
        const statusMensagens = {
          'em andamento': 'Seu pedido est\u00e1 sendo preparado!',
          'pronto': 'Seu pedido est\u00e1 pronto para retirada!',
          'entregue': 'Seu pedido foi entregue!',
          'cancelado': 'Seu pedido foi cancelado.'
        };

        const novoStatus = data.pedido.status?.toLowerCase();
        const mensagem = statusMensagens[novoStatus];

        if (mensagem && 'Notification' in window && Notification.permission === 'granted') {
          new Notification(mensagem, {
            body: `Pedido #${data.pedido.id_pedido}`,
            icon: '/logo192.png',
            tag: `pedido-status-${data.pedido.id_pedido}`,
            requireInteraction: novoStatus === 'pronto'
          });
        }
      }

      // Atualizar o pedido na lista
      setPedidos((prevPedidos) =>
        prevPedidos.map((pedido) =>
          pedido.id_pedido === data.pedido.id_pedido
            ? { ...pedido, ...data.pedido }
            : pedido
        )
      );
    };

    sseService.on('novo_pedido', handleNovoPedido);
    sseService.on('pedido_atualizado', handlePedidoAtualizado);

    // 🔄 Polling de fallback: garante atualização mesmo quando SSE falha em produção
    // (proxies reversos de hospedagem compartilhada costumam bloquear/bufferizar SSE)
    const pollingInterval = setInterval(() => {
      fetchPedidos();
    }, 15000);

    // Remover listeners ao desmontar (não desconectar - SSE é compartilhado)
    return () => {
      clearInterval(pollingInterval);
      window.removeEventListener('loginSuccess', handleLoginSuccess);
      window.removeEventListener('estabelecimentoChanged', handleEstabelecimentoChanged);
      sseService.off('novo_pedido', handleNovoPedido);
      sseService.off('pedido_atualizado', handlePedidoAtualizado);
      console.log('🔌 [Context] Listeners SSE removidos');
    };
  }, []);

return (
  <PedidosContext.Provider value={{ pedidos, setPedidos, loading, atualizarPedidos }}>
    {children}
  </PedidosContext.Provider>
);

}

export default PedidosContext;
